import { useEffect, useMemo, useReducer, useState } from 'react';
import AboutPulinaPage from './partials/AboutPulinaPage';
import HomePageContent from './partials/HomePageContent';
import NotFoundPage from './partials/NotFoundPage';
import { useAuth } from './AuthModule';
import CreateEventView from './CreateEventView';
import ChatView from './ChatView';
import { EditEventPage, EventShowPage } from './partials/EditEventView';
import SubmitQuestionsView from './SubmitQuestionsView';
import EventView from './EventView';
import DashboardPage from './DashboardPage';
import ForumPageView from './ForumPageView';
import ForumPostViewPage from './ForumPostViewPage';
import EventLivestreamPage from './EventLivestreamPage';
import EventChatPage from './EventChatPage';
import UserSettingsPage from './partials/UserSettingsPage';
import KauppaPage from './partials/KauppaPage';
import UserProfilePage from './partials/UserProfilePage';
import PublicProfileView from './partials/PublicProfileView';
import PostilaatikkoPage from './partials/PostilaatikkoPage';
import KaveritPage from './partials/KaveritPage';
import TilastotPage from './partials/TilastotPage';
import TyokalutPage from './partials/TyokalutPage';
import AdminDailyQuestionPage from './partials/AdminDailyQuestionPage';
import AdminCreditDonatePage from './partials/AdminCreditDonatePage';
import AdminTriviaSuggestionsPage from './partials/AdminTriviaSuggestionsPage';
import AdminBetaUsersPage from './partials/AdminBetaUsersPage';
import AdminUsersPage from './partials/AdminUsersPage';
import TriviaSuggestionModal from './partials/TriviaSuggestionModal';
import SubmitDailyQuestionModal from './partials/SubmitDailyQuestionModal';
import EventPromoModal from './partials/EventPromoModal';
import PostPromoModal from './partials/PostPromoModal';
import EmojiGameOptimizerPage from './partials/EmojiGameOptimizerPage';
import TarjousPage from './partials/TarjousPage';
import PasswordResetPage from './partials/PasswordResetPage';
import RegisterPage from './partials/RegisterPage';
import SetPasswordPage from './partials/SetPasswordPage';
import PersonalMailView from './partials/PersonalMailView';
import OhjeetPage from './partials/OhjeetPage';
import BetaTestaajaksiPage from './partials/BetaTestaajaksiPage';
// Use backend URL. Empty string = same origin (proxy in dev). Else set REACT_APP_API_URL e.g. http://localhost:3001
const API = process.env.REACT_APP_API_URL || '';

const GAME_SUGGESTION_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function formatEventDay(eventDate) {
  if (!eventDate || !/^\d{4}-\d{2}-\d{2}$/.test(eventDate)) return { base: '—', label: null };
  const [y, m, d] = eventDate.split('-').map((x) => parseInt(x, 10));
  const dd = String(d).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const now = new Date();
  const currentYear = now.getFullYear();
  let base = `${dd}. ${mm}.`;
  if (y !== currentYear) base += ` ${y}`;
  const todayStr = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.getFullYear() + '-' + String(tomorrow.getMonth() + 1).padStart(2, '0') + '-' + String(tomorrow.getDate()).padStart(2, '0');
  const label = eventDate === todayStr ? 'Tänään' : eventDate === tomorrowStr ? 'Huomenna' : null;
  return { base, label };
}

function getEventStartTime(ev) {
  const [hh, mm = 0] = (ev.time || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  const d = new Date();
  if (ev.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(ev.eventDate)) {
    const [y, m, day] = ev.eventDate.split('-').map((x) => parseInt(x, 10));
    d.setFullYear(y, m - 1, day);
  }
  d.setHours(hh, mm, 0, 0);
  return d;
}

function isEventUpcoming(ev) {
  return getEventStartTime(ev).getTime() > Date.now();
}

function PageContentRoutes() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [showCreateEventView, setShowCreateEventView] = useState(false);
  const [submitQuestionsEvent, setSubmitQuestionsEvent] = useState(null);
  const [eventViewEvent, setEventViewEvent] = useState(null);
  const rawPath = window.location.pathname || '/';
  const path = rawPath.replace(/^\/+/, '/');
  const isSubmitDailyLanding = path === '/submitDaily' || path === '/submitDaily/';
  const isEventPromoLanding = path === '/eventPromo' || path === '/eventPromo/';
  const isPostPromoLanding = path === '/postPromo' || path === '/postPromo/';

  // Load events from API when on dashboard; refresh every 10 minutes
  useEffect(() => {
    if ((path !== '/' && !isSubmitDailyLanding && !isEventPromoLanding && !isPostPromoLanding) || !user) return;
    const fetchEvents = () => {
      fetch(`${API}/api/events`)
        .then((res) => res.json())
        .then((data) => setEvents(Array.isArray(data) ? data : []))
        .catch(() => setEvents([]));
    };
    fetchEvents();
    const interval = setInterval(fetchEvents, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [path, user, isSubmitDailyLanding, isEventPromoLanding, isPostPromoLanding]);

  const upcomingEvents = useMemo(() => {
    const upcoming = events.filter(isEventUpcoming);
    return upcoming
      .slice()
      .sort(
        (a, b) =>
          getEventStartTime(a).getTime() - getEventStartTime(b).getTime()
      );
  }, [events]);

  if (path === '/' || isSubmitDailyLanding || isEventPromoLanding || isPostPromoLanding) {
    if (user) {
      const isPrivileged = !!user.role && user.role !== 'user';

      if (showCreateEventView) {
        return (
          <CreateEventView
            user={user}
            onCancel={() => setShowCreateEventView(false)}
            onSaved={(updatedEvents) => {
              setEvents(Array.isArray(updatedEvents) ? updatedEvents : []);
              setShowCreateEventView(false);
            }}
          />
        );
      }

      if (submitQuestionsEvent) {
        return (
          <SubmitQuestionsView
            event={submitQuestionsEvent}
            user={user}
            onCancel={() => setSubmitQuestionsEvent(null)}
            onSubmitted={() => setSubmitQuestionsEvent(null)}
          />
        );
      }

      if (eventViewEvent) {
        return (
          <EventView
            event={eventViewEvent}
            onBack={() => setEventViewEvent(null)}
            user={user}
            onEventUpdated={(updatedEvents) => {
              if (Array.isArray(updatedEvents)) {
                setEvents(updatedEvents);
                const updated = updatedEvents.find((e) => e.id === eventViewEvent.id);
                if (updated) setEventViewEvent(updated);
              }
            }}
            onEventDeleted={(deletedId) => {
              setEventViewEvent(null);
              setEvents((prev) => prev.filter((e) => e.id !== deletedId));
            }}
          />
        );
      }

      return (
        <DashboardPage
          path={path}
          user={user}
          allEvents={events}
          upcomingEvents={upcomingEvents}
          formatEventDay={formatEventDay}
          onOpenChat={() => isPrivileged && (window.location.href = '/chat/')}
          onOpenCreateEvent={() => setShowCreateEventView(true)}
          onOpenEventView={(ev) => setEventViewEvent(ev)}
        />
      );
    }

    return <HomePageContent />;
  }

  if (path === '/chat/') {
    return <ChatView />;
  }

  if (path === '/beta-testaajaksi/ohjeet' || path === '/beta-testaajaksi/ohjeet/') {
    return <OhjeetPage ohjeetBase="/beta-testaajaksi/ohjeet" />;
  }
  if (path === '/beta-testaajaksi/pulinasta' || path === '/beta-testaajaksi/pulinasta/') {
    return <AboutPulinaPage />;
  }
  if (path === '/beta-testaajaksi/tarjous' || path === '/beta-testaajaksi/tarjous/') {
    return <TarjousPage backToBetaHome />;
  }
  if (path === '/beta-testaajaksi' || path === '/beta-testaajaksi/') {
    return <BetaTestaajaksiPage />;
  }

  const forumPostMatch = path.match(/^\/forum\/post\/([^/]+)\/?$/);
  if (forumPostMatch) {
    if (!user) {
      return (
        <div className="App-block">
          <p>Kirjaudu sisään nähdäksesi ilmoituksen.</p>
        </div>
      );
    }
    return (
      <ForumPostViewPage
        postId={decodeURIComponent(forumPostMatch[1])}
        user={user}
        onBackToList={() => {
          window.location.href = '/forum/';
        }}
      />
    );
  }

  if (path === '/forum/' || path === '/forum') {
    if (!user) {
      return (
        <div className="App-block">
          <p>Kirjaudu sisään nähdäksesi ilmoitustaulun.</p>
        </div>
      );
    }
    return <ForumPageView user={user} onBackToDashboard={() => (window.location.href = '/')} />;
  }

  // Handle /event/show/:event_id route — show EventView (read-only event details)
  if (path.startsWith('/event/show/')) {
    const showMatch = path.match(/^\/event\/show\/([^/]+)$/);
    if (showMatch && user) {
      return <EventShowPage eventId={showMatch[1]} user={user} />;
    }
  }

  // Handle /event/edit/:event_id route — show EditEventView
  if (path.startsWith('/event/edit/')) {
    const editMatch = path.match(/^\/event\/edit\/([^/]+)$/);
    if (editMatch && user) {
      return <EditEventPage eventId={editMatch[1]} user={user} />;
    }
  }

  // Handle /event/:event_id/chat — event chat in new tab
  const eventChatMatch = path.match(/^\/event\/([^/]+)\/chat$/);
  if (eventChatMatch && eventChatMatch[1] !== 'edit' && eventChatMatch[1] !== 'show' && eventChatMatch[1] !== 'stream') {
    return <EventChatPage eventId={eventChatMatch[1]} />;
  }

  // Handle /event/stream/:event_id — livestream
  if (path.startsWith('/event/stream/')) {
    const streamMatch = path.match(/^\/event\/stream\/([^/]+)$/);
    if (streamMatch) {
      return <EventLivestreamPage eventId={streamMatch[1]} />;
    }
  }

  // Handle /event/:event_id — livestream (backward compat)
  if (path.startsWith('/event/')) {
    const eventIdMatch = path.match(/^\/event\/([^/]+)$/);
    if (eventIdMatch) {
      const eventId = eventIdMatch[1];
      if (eventId !== 'edit' && eventId !== 'show' && eventId !== 'stream') {
        return <EventLivestreamPage eventId={eventId} />;
      }
    }
  }

  const mailMatch = path.match(/^\/mail\/(\d+)\/?$/);
  if (mailMatch && user) {
    const messageId = mailMatch[1];
    const searchParams = new URLSearchParams(window.location.search);
    const eventRatingEventId = searchParams.get('rate-event') || undefined;
    return <PersonalMailView messageId={messageId} eventRatingEventId={eventRatingEventId} />;
  }
  if (path === '/asetukset') {
    if (!user) {
      return (
        <div className="App-block">
          <p>Kirjaudu sisään muuttaaksesi asetuksia.</p>
        </div>
      );
    }
    return <UserSettingsPage user={user} />;
  }

  const isKauppaPath =
    path === '/kauppa' || path === '/kauppa/' || path === '/krediitit' || path === '/krediitit/';
  if (isKauppaPath) {
    if (!user) {
      return (
        <div className="App-block">
          <p>Kirjaudu sisään nähdäksesi Pulina-krediitit.</p>
        </div>
      );
    }
    const isMember = !!user.role && user.role !== 'user';
    if (!isMember) {
      return (
        <div className="App-block">
          <p>Krediittikauppa on vain jäsenille.</p>
        </div>
      );
    }
    return <KauppaPage user={user} />;
  }

  if (path === '/postilaatikko') {
    if (!user) {
      return (
        <div className="App-block">
          <p>Kirjaudu sisään nähdäksesi postilaatikkosi.</p>
        </div>
      );
    }
    return <PostilaatikkoPage />;
  }

  if (path === '/kaverit' || path === '/kaverit/') {
    if (!user) {
      return (
        <div className="App-block">
          <p>Kirjaudu sisään nähdäksesi kaverisi.</p>
        </div>
      );
    }
    return <KaveritPage />;
  }

  if (path === '/profiili') {
    if (!user) {
      return (
        <div className="App-block">
          <p>Kirjaudu sisään nähdäksesi profiilin.</p>
        </div>
      );
    }
    return <UserProfilePage user={user} />;
  }

  const profileUserIdMatch = path.match(/^\/profiili\/(\d+)\/?$/);
  if (profileUserIdMatch) {
    const profileUserId = profileUserIdMatch[1];
    if (!user) {
      return (
        <div className="App-block">
          <p>Kirjaudu sisään nähdäksesi profiilin.</p>
        </div>
      );
    }
    const currentUserId = user.id != null ? String(user.id).replace(/n$/, '') : '';
    if (currentUserId !== profileUserId) {
      return (
        <div className="App-block">
          <p>Voit tarkastella vain omaa profiilia.</p>
        </div>
      );
    }
    return <UserProfilePage user={user} />;
  }

  // Handle /profiili/:displayName route — public profile view by unique displayName
  const profileDisplayNameMatch = path.match(/^\/profiili\/(?!\d+\/?$)([^/]+)\/?$/);
  if (profileDisplayNameMatch) {
    const pathDisplayName = decodeURIComponent(profileDisplayNameMatch[1]);
    const searchParams = new URLSearchParams(window.location.search);
    const redirect = searchParams.get('redirect');
    return <PublicProfileView displayName={pathDisplayName} redirect={redirect || undefined} />;
  }

  if (path === '/tilastot') {
    return <TilastotPage />;
  }

  if (path === '/admin/paivan-kysymys' || path === '/admin/paivan-kysymys/') {
    return <AdminDailyQuestionPage />;
  }

  if (path === '/admin/tarkasta-ehdotukset' || path === '/admin/tarkasta-ehdotukset/') {
    return <AdminTriviaSuggestionsPage />;
  }

  if (path === '/admin/lahjoita-krediitteja' || path === '/admin/lahjoita-krediitteja/') {
    return <AdminCreditDonatePage />;
  }

  if (path === '/admin/beta-testaajat' || path === '/admin/beta-testaajat/') {
    return <AdminBetaUsersPage />;
  }

  if (path === '/admin/users' || path === '/admin/users/') {
    return <AdminUsersPage />;
  }

  if (path === '/admin' || path === '/admin/') {
    if (!user) {
      return (
        <div className="App-block">
          <p>Kirjaudu sisään nähdäksesi hallintatyökalut.</p>
        </div>
      );
    }
    return <TyokalutPage />;
  }

  if (path === '/emoji-optimizer') {
    return <EmojiGameOptimizerPage />;
  }

  if (path === '/tarjous') {
    return <TarjousPage />;
  }

  if (path === '/ohjeet' || path === '/ohjeet/') {
    if (!user) {
      return (
        <div className="App-block">
          <p>Ohjeet-sivu on vain kirjautuneille käyttäjille.</p>
        </div>
      );
    }
    return <OhjeetPage />;
  }

  if (path === '/pulinasta') {
    return <AboutPulinaPage />;
  }


  if (path === '/password-reset') {
    return <PasswordResetPage />;
  }

  if (path === '/uusi') {
    return <RegisterPage />;
  }

  if (path === '/set-password') {
    return <SetPasswordPage />;
  }

  if (path === '/') {
    return <HomePageContent />;
  }

  return <NotFoundPage />;
}

function PageContent() {
  const { user } = useAuth();
  const [gameSuggestionId, setGameSuggestionId] = useState(null);
  const [submitDailyModalOpen, setSubmitDailyModalOpen] = useState(false);
  const [eventPromoToken, setEventPromoToken] = useState(null);
  const [postPromoToken, setPostPromoToken] = useState(null);
  const [, bumpPath] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    if (!user || !user.role || user.role === 'user') {
      setSubmitDailyModalOpen(false);
      setEventPromoToken(null);
      setPostPromoToken(null);
    }
  }, [user]);

  useEffect(() => {
    const consumeSubmitDailyPath = () => {
      const p = (window.location.pathname || '/').replace(/^\/+/, '/');
      if (p !== '/submitDaily' && p !== '/submitDaily/') return;
      if (user && user.role && user.role !== 'user') {
        setSubmitDailyModalOpen(true);
      } else {
        setSubmitDailyModalOpen(false);
      }
      window.history.replaceState(
        {},
        '',
        '/' + window.location.search + window.location.hash,
      );
      bumpPath();
    };
    consumeSubmitDailyPath();
    window.addEventListener('popstate', consumeSubmitDailyPath);
    return () => window.removeEventListener('popstate', consumeSubmitDailyPath);
  }, [user]);

  useEffect(() => {
    const EVENT_PROMO_TOKEN_RE =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const consumeEventPromoPath = () => {
      const p = (window.location.pathname || '/').replace(/^\/+/, '/');
      if (p !== '/eventPromo' && p !== '/eventPromo/') return;
      const sp = new URLSearchParams(window.location.search);
      const tok = sp.get('token')?.trim();
      if (user && user.role && user.role !== 'user' && tok && EVENT_PROMO_TOKEN_RE.test(tok)) {
        setEventPromoToken(tok);
      } else {
        setEventPromoToken(null);
      }
      sp.delete('token');
      const q = sp.toString();
      window.history.replaceState({}, '', `/${q ? `?${q}` : ''}${window.location.hash}`);
      bumpPath();
    };
    consumeEventPromoPath();
    window.addEventListener('popstate', consumeEventPromoPath);
    return () => window.removeEventListener('popstate', consumeEventPromoPath);
  }, [user]);

  useEffect(() => {
    const EVENT_PROMO_TOKEN_RE =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const consumePostPromoPath = () => {
      const p = (window.location.pathname || '/').replace(/^\/+/, '/');
      if (p !== '/postPromo' && p !== '/postPromo/') return;
      const sp = new URLSearchParams(window.location.search);
      const tok = sp.get('token')?.trim();
      if (user && user.role && user.role !== 'user' && tok && EVENT_PROMO_TOKEN_RE.test(tok)) {
        setPostPromoToken(tok);
      } else {
        setPostPromoToken(null);
      }
      sp.delete('token');
      const q = sp.toString();
      window.history.replaceState({}, '', `/${q ? `?${q}` : ''}${window.location.hash}`);
      bumpPath();
    };
    consumePostPromoPath();
    window.addEventListener('popstate', consumePostPromoPath);
    return () => window.removeEventListener('popstate', consumePostPromoPath);
  }, [user]);

  useEffect(() => {
    if (!user) {
      setGameSuggestionId(null);
      return;
    }
    const consumeGameSuggestionParam = () => {
      const sp = new URLSearchParams(window.location.search);
      const gs = sp.get('gameSuggestion')?.trim();
      if (!gs || !GAME_SUGGESTION_UUID_RE.test(gs)) return;
      setGameSuggestionId(gs);
      sp.delete('gameSuggestion');
      const q = sp.toString();
      const clean = `${window.location.pathname}${q ? `?${q}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', clean);
    };
    consumeGameSuggestionParam();
    window.addEventListener('popstate', consumeGameSuggestionParam);
    return () => window.removeEventListener('popstate', consumeGameSuggestionParam);
  }, [user]);

  return (
    <>
      <PageContentRoutes />
      {gameSuggestionId && user ? (
        <TriviaSuggestionModal
          user={user}
          suggestionId={gameSuggestionId}
          onClose={() => setGameSuggestionId(null)}
          onSubmitted={() => setGameSuggestionId(null)}
        />
      ) : null}
      {submitDailyModalOpen && user && user.role && user.role !== 'user' ? (
        <SubmitDailyQuestionModal
          user={user}
          onClose={() => setSubmitDailyModalOpen(false)}
          onSubmitted={() => setSubmitDailyModalOpen(false)}
        />
      ) : null}
      {eventPromoToken && user && user.role && user.role !== 'user' ? (
        <EventPromoModal
          user={user}
          token={eventPromoToken}
          onClose={() => setEventPromoToken(null)}
        />
      ) : null}
      {postPromoToken && user && user.role && user.role !== 'user' ? (
        <PostPromoModal
          user={user}
          token={postPromoToken}
          onClose={() => setPostPromoToken(null)}
        />
      ) : null}
    </>
  );
}

export default PageContent;

